import { Input } from "@majitap/ui/arc/input/input";
import { Select } from "@majitap/ui/arc/select/select";
import { FULFILLMENT, type Fulfillment, type NewItem, UNIT, type Unit } from "../../products";
import styles from "./products.module.css";

export interface ItemDraft {
  name: string;
  sku: string;
  unit: Unit;
  unit_size: string;
  fulfillment: Fulfillment;
}

export const blankItem = (water: boolean): ItemDraft => ({
  name: "",
  sku: "",
  unit: water ? "litre" : "piece",
  unit_size: water ? "10000" : "1",
  fulfillment: water ? "truck_load" : "parcel",
});

/** What's wrong with a new item, field by field: the API's lengths, and a size above zero (up to 3 decimals). */
export const itemErrors = (i: ItemDraft) => ({
  itemName: !i.name.trim()
    ? "Enter the item name."
    : i.name.trim().length > 120
      ? "At most 120 characters."
      : undefined,
  sku: !i.sku.trim() ? "Enter the SKU." : i.sku.trim().length > 64 ? "At most 64 characters." : undefined,
  // DECIMAL(14,3): up to 11 whole digits
  unitSize:
    /^\d{1,11}(\.\d{1,3})?$/.test(i.unit_size) && /[1-9]/.test(i.unit_size)
      ? undefined
      : "A size above 0, with at most 3 decimals.",
});

/** An item the API takes: no field has a problem. */
export const itemReady = (i: ItemDraft) => Object.values(itemErrors(i)).every((e) => !e);

export const toNewItem = (i: ItemDraft): NewItem => ({
  name: i.name.trim(),
  sku: i.sku.trim(),
  unit: i.unit,
  unit_size: i.unit_size,
  fulfillment: i.fulfillment,
});

/** The fields of a new item. Unit, size and fulfilment can't change later: a new item replaces it. */
export function ItemFields({
  item,
  onChange,
  show,
}: {
  item: ItemDraft;
  onChange: (item: ItemDraft) => void;
  /** The form check's `show`: errors wait for the first press. */
  show: (error: string | undefined) => string | undefined;
}) {
  const errors = itemErrors(item);
  const set = (patch: Partial<ItemDraft>) => onChange({ ...item, ...patch });
  return (
    <>
      <div className={styles.pair}>
        <Input
          label="Item name"
          value={item.name}
          onChange={(e) => set({ name: e.target.value })}
          placeholder="10,000 L"
          error={show(errors.itemName)}
        />
        <Input
          label="SKU"
          value={item.sku}
          onChange={(e) => set({ sku: e.target.value })}
          placeholder="W10K"
          description="Unique in the company."
          error={show(errors.sku)}
        />
      </div>
      <div className={styles.pair}>
        <Select
          label="Unit"
          value={item.unit}
          onValueChange={(v) => set({ unit: v as Unit })}
          options={(Object.keys(UNIT) as Unit[]).map((u) => ({ value: u, label: UNIT[u] }))}
        />
        <Input
          label="Size"
          inputMode="decimal"
          value={item.unit_size}
          onChange={(e) => set({ unit_size: e.target.value })}
          description="How many units one item is, e.g. 10000 litres."
          error={show(errors.unitSize)}
        />
      </div>
      <Select
        label="Fulfilment"
        value={item.fulfillment}
        onValueChange={(v) => set({ fulfillment: v as Fulfillment })}
        description={FULFILLMENT[item.fulfillment].help}
        options={(Object.keys(FULFILLMENT) as Fulfillment[]).map((f) => ({ value: f, label: FULFILLMENT[f].label }))}
      />
    </>
  );
}
